(() => {
  class CartService {
    constructor() {
      this.eventTarget = new EventTarget();
      this.pendingRequests = 0;
      this.idleResolvers = [];
      this.cartPromise = null;
      this.cartSnapshot = null;

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source !== 'express-cart-service') {
            this.invalidateCart();
          }
        });
      }
    }

    on(eventName, callback) {
      this.eventTarget.addEventListener(eventName, callback);
      return () => this.eventTarget.removeEventListener(eventName, callback);
    }

    emit(eventName, detail = {}) {
      const payload = Object.assign({}, detail || {}, { service: this });
      this.eventTarget.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
      document.dispatchEvent(new CustomEvent(`cart:${eventName}`, { detail: payload }));
    }

    invalidateCart() {
      this.cartPromise = null;
      this.cartSnapshot = null;
    }

    async waitForIdle() {
      if (this.pendingRequests === 0) return;

      await new Promise((resolve) => {
        this.idleResolvers.push(resolve);
      });
    }

    async getCart(options = {}) {
      const { force = false } = options;

      if (force) {
        this.invalidateCart();
      }

      if (this.cartSnapshot) {
        return this.cartSnapshot;
      }

      if (!this.cartPromise) {
        this.cartPromise = this.fetchJson(`${routes.cart_url}.js`, {
          headers: { Accept: 'application/json' },
        }).then((cart) => {
          this.cartSnapshot = cart;
          return cart;
        }).catch((error) => {
          this.cartPromise = null;
          throw error;
        });
      }

      return this.cartPromise;
    }

    async changeLine(body = {}) {
      this.invalidateCart();

      const responseText = await this.trackRequest(async () => {
        return fetch(`${routes.cart_change_url}`, Object.assign({}, fetchConfig(), {
          body: JSON.stringify({
            line: body.line,
            quantity: body.quantity,
            sections: body.sections || [],
            sections_url: body.sections_url || window.location.pathname,
          }),
        })).then((response) => response.text());
      }, {
        operation: 'change',
        line: body.line,
        quantity: body.quantity,
      });

      const cartState = JSON.parse(responseText);
      this.emit('updated', {
        operation: 'change',
        cart: cartState,
        line: body.line,
        quantity: body.quantity,
      });

      return cartState;
    }

    async clear() {
      this.invalidateCart();

      const cartState = await this.trackRequest(async () => {
        return this.fetchJson(`${routes.cart_url}/clear.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
        });
      }, {
        operation: 'clear',
      });

      this.emit('updated', {
        operation: 'clear',
        cart: cartState,
      });

      return cartState;
    }

    normalizeLineItemProperties(properties = {}) {
      return Object.entries(properties || {}).reduce((normalized, [key, value]) => {
        if (value === null || typeof value === 'undefined') return normalized;

        normalized[key] = typeof value === 'object' ? JSON.stringify(value) : String(value);
        return normalized;
      }, {});
    }

    async parseCartResponse(response) {
      const responseText = await response.text();

      if (!responseText) return {};

      try {
        return JSON.parse(responseText);
      } catch (_error) {
        return responseText;
      }
    }

    buildCartAddBody(input = {}) {
      const body = {
        id: input.id,
        quantity: input.quantity || 1,
        properties: this.normalizeLineItemProperties(input.properties || {}),
      };

      if (Array.isArray(input.sections) && input.sections.length) {
        body.sections = input.sections;
      }

      if (input.sections_url) {
        body.sections_url = input.sections_url;
      }

      return body;
    }

    async addItem(input = {}, options = {}) {
      this.invalidateCart();

      const rawCartAddUrl = options.cartAddUrl || input.cartAddUrl || window.routes?.cart_add_url || '/cart/add';
      const cartAddUrl = rawCartAddUrl.endsWith('.js') ? rawCartAddUrl : `${rawCartAddUrl}.js`;
      const body = this.buildCartAddBody(input);

      if (!body.id) {
        throw new Error('Missing variant id for cart add request.');
      }

      const item = await this.trackRequest(async () => {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        const data = await this.parseCartResponse(response);
        const apiMessage = typeof data === 'object' && data !== null
          ? data.description || data.message
          : '';

        if (!response.ok || (typeof data === 'object' && data !== null && data.status)) {
          throw new Error(apiMessage || `Cart add request failed with status ${response.status}`);
        }

        return data;
      }, {
        operation: 'add',
        id: body.id,
        quantity: body.quantity,
      });

      this.emit('updated', {
        operation: 'add',
        cart: item,
        item,
      });

      return item;
    }

    async updateNote(note) {
      await this.trackRequest(async () => {
        return fetch(`${routes.cart_update_url}`, Object.assign({}, fetchConfig(), {
          body: JSON.stringify({ note }),
        }));
      }, {
        operation: 'update-note',
      });

      this.invalidateCart();
    }

    async fetchJson(url, options = {}) {
      const response = await fetch(url, options);

      if (!response.ok) {
        throw new Error(`Cart request failed with status ${response.status}`);
      }

      return response.json();
    }

    async trackRequest(callback, detail = {}) {
      this.pendingRequests += 1;
      this.emit('request:start', detail);

      try {
        return await callback();
      } catch (error) {
        this.emit('request:error', Object.assign({}, detail || {}, { error }));
        throw error;
      } finally {
        this.pendingRequests -= 1;
        this.emit('request:end', detail);

        if (this.pendingRequests === 0) {
          this.idleResolvers.splice(0).forEach((resolve) => resolve());
        }
      }
    }
  }

  window.Express = window.Express || {};
  window.Express.CartService = window.Express.CartService || CartService;
  window.Express.cartService = window.Express.cartService || new CartService();
})();
